import { useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { FiSearch, FiEdit2, FiTrash2 } from "react-icons/fi";
import { IoChevronDown } from "react-icons/io5";
import { ModuleLayout } from "../../../layouts";
import { ContentCard, Button, Pagination } from "../../../components";
import { ROUTES_CONSTANTS, QUERY_CONSTANTS } from "../../../constants";
import { useAuth } from "../../../context";
import {
  StatCard,
  StatusBadge,
  SectionHeader,
  DataTable,
  ActionIconButton,
  ConfirmModal,
} from "../components";
import { getCompanyUsersApi, deleteUserApi } from "../../../DAL";

const UsersList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const companyId = currentUser?.company?.id;

  const TABLE_COLS = [t('users.colUser'), t('users.colRole'), t('users.colDepartment'), t('common.status'), t('common.lastActive'), t('common.actions')];

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ── Data ────────────────────────────────────────────────────────────────────
  const { data: usersData, isLoading } = useQuery({
    queryKey: [QUERY_CONSTANTS.USERS, page],
    queryFn: () => getCompanyUsersApi(companyId, page),
    enabled: !!companyId,
  });
  const users = usersData?.data ?? [];
  const stats = usersData?.stats ?? {};
  const pagination = usersData?.pagination ?? {};

  const statCards = [
    { value: stats.total ?? pagination.total ?? users.length, label: t('users.statTotal') },
    { value: stats.active ?? 0, label: t('common.active') },
    { value: stats.pending ?? 0, label: t('common.pending') },
    { value: stats.inactive ?? 0, label: t('common.inactive') },
  ];

  // ── Filter ──────────────────────────────────────────────────────────────────
  const filtered = users.filter((u) => {
    const matchSearch =
      !search ||
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase());
    const matchStatus =
      !statusFilter ||
      u.status?.toLowerCase() === statusFilter.toLowerCase();
    return matchSearch && matchStatus;
  });

  // ── Delete ───────────────────────────────────────────────────────────────────
  const { mutate: deleteUser, isPending: isDeleting } = useMutation({
    mutationFn: () => deleteUserApi(companyId, deleteTarget.id),
    onSuccess: (response) => {
      enqueueSnackbar(response?.message, { variant: "success" });
      queryClient.invalidateQueries({ queryKey: [QUERY_CONSTANTS.USERS] });
      setDeleteTarget(null);
    },
  });

  return (
    <ModuleLayout
      title={t('users.title')}
      onBack={() => navigate(ROUTES_CONSTANTS.COMPANY_DASHBOARD)}
    >
      <p className="text-sm text-gray-500 mb-4">{t('users.breadcrumb')}</p>

      <ContentCard>
        <SectionHeader
          title={t('users.sectionTitle')}
          subtitle={t('users.sectionSubtitle')}
          action={
            <Button onClick={() => navigate(ROUTES_CONSTANTS.INVITE_USER)}>
              {t('users.btnAdd')}
            </Button>
          }
        />

        {/* Stats */}
        <div className="flex gap-4 mb-6">
          {statCards.map((s) => (
            <StatCard key={s.label} value={s.value} label={s.label} />
          ))}
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-4">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('common.searchPlaceholder')}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full border border-[#E3E3E3] rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-primary-500"
            />
          </div>
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="appearance-none border border-[#E3E3E3] rounded-lg pl-3 pr-8 py-2 text-sm bg-white focus:outline-none cursor-pointer"
            >
              <option value="">{t('common.allStatus')}</option>
              <option value="active">{t('common.active')}</option>
              <option value="pending">{t('common.pending')}</option>
              <option value="inactive">{t('common.inactive')}</option>
            </select>
            <IoChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none w-4 h-4" />
          </div>
        </div>

        <DataTable columns={TABLE_COLS} isLoading={isLoading}>
          {filtered.map((user) => (
            <tr key={user.id} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <p className="text-sm font-medium text-black">{user.name}</p>
                <p className="text-xs text-gray-400">{user.email}</p>
              </td>
              <td className="px-4 py-3 text-sm text-gray-700">
                {user.role?.display_name ?? "—"}
              </td>
              <td className="px-4 py-3 text-sm text-gray-700">
                {user.department?.name ?? "—"}
              </td>
              <td className="px-4 py-3">
                <StatusBadge
                  status={
                    user.status
                      ? user.status.charAt(0).toUpperCase() + user.status.slice(1)
                      : "—"
                  }
                />
              </td>
              <td className="px-4 py-3 text-sm text-gray-500">
                {user.updated_at
                  ? new Date(user.updated_at).toLocaleDateString()
                  : "—"}
              </td>
              <td className="px-4 py-3">
                <div className="flex gap-2">
                  <ActionIconButton
                    icon={FiEdit2}
                    onClick={() =>
                      navigate(ROUTES_CONSTANTS.UPDATE_USER, {
                        state: { id: user.id },
                      })
                    }
                  />
                  <ActionIconButton
                    icon={FiTrash2}
                    onClick={() => setDeleteTarget(user)}
                    danger
                  />
                </div>
              </td>
            </tr>
          ))}
        </DataTable>

        <Pagination
          currentPage={pagination.current_page ?? page}
          lastPage={pagination.last_page}
          onPageChange={setPage}
        />
      </ContentCard>

      {deleteTarget && (
        <ConfirmModal
          title={t('users.deleteTitle')}
          confirmLabel={t('common.delete')}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={deleteUser}
          loading={isDeleting}
        >
          <p>
            {t('users.deleteConfirmPre')}{" "}
            <span className="text-primary-500 font-medium">
              [{deleteTarget.name}]
            </span>{" "}
            {t('users.deleteConfirmPost')}
          </p>
        </ConfirmModal>
      )}
    </ModuleLayout>
  );
};

export default UsersList;
